import "react-datepicker/dist/react-datepicker.css";
import clsx from "clsx";
import _ from "lodash";
import moment from "moment/moment";
import React, { useMemo, useState } from "react";
import DatePicker from "react-datepicker";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import actions from "@/actions";
import { FormGrid, FormGroup, FormRow, Spacer } from "@/components/common";
import { CustomDatePickerInput } from "@/components/common/CustomDatePickerInput";
import { MasterSelect } from "@/components/common/MasterSelect";
import { FilterConfigGroup } from "@/components/common/SearchCondition";
import { SearchConditionConfigModal } from "@/components/common/SearchCondition/SearchConditionConfigModal";
import { SearchConditionDisplayButton } from "@/components/common/SearchCondition/SearchConditionDisplayButton";
import { SettingIconButton } from "@/components/common/SettingIconButton";
import { UseSearchInputType } from "@/components/list/Schedule/hooks/useSearchInput";
import { SearchConditionOption, SearchInput } from "@/components/list/Schedule/types";
import { ScheduleSearchKey, ScheduleSearchKeyType } from "@/components/list/Schedule/types/schedule";
import { NONE_VALUE } from "@/constants";
import { useSearchConditionConfig } from "@/hooks/useSearchConditionConfig";
import storageManager from "@/lib/storageManager";
import { FilterConfig } from "@/models/filterConfig";
import { ConstructionMasters } from "@/models/masters";
import { SearchConditionConfigDefinition, ScreenName } from "@/models/screenConfig";
import { RootState } from "@/reducers/types";
import { ExplanatoryNote } from "@/components/list/Schedule/components/ExplanatoryNote";
import { FireTypeAbbreviationNote } from "@/components/list/Schedule/components/FireTypeAbbreviationNote";

type Props = {
  scheduleDate: Date;
  input: UseSearchInputType;
  masters: ConstructionMasters;
  searchOptions: SearchConditionOption[];
  onReset: () => void;
  onSearch: (option?: { params?: SearchInput }) => void;
};

export const SearchBox = ({ scheduleDate, input, masters, searchOptions, onReset, onSearch }: Props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [scheduleDateLabel, setScheduleDateLabel] = useState<string>(moment(scheduleDate).format("YYYY/MM/DD"));

  // 予定表項目情報
  const scheduleFields = useSelector((state: RootState) => state.schedule.fields);
  const fieldLabelMap = useMemo(() => {
    return new Map(
      scheduleFields
        .filter((field) => ["field1", "field2", "field3", "approve1", "approve4", "field_t9"].includes(field.field_key))
        .map((field) => {
          const locale = storageManager.getLocale();
          const customLabel = locale === "ja" ? field.field_label_jp : field.field_label_en;
          let label = "";
          if (field.field_key === "field1") {
            label = customLabel || t("hazard") + t("rank");
          } else if (field.field_key === "field2") {
            label = customLabel || t("attendance");
          } else if (field.field_key === "field3") {
            label = customLabel || t("company");
          } else if (field.field_key === "field_t9") {
            label = customLabel || t("overtime_schedule");
          } else if (field.field_key === "approve1") {
            label = customLabel || t("construction") + t("supervising_section");
          } else if (field.field_key === "approve4") {
            label = customLabel || t("facility") + t("supervising_section");
          }
          return [field.field_key, label];
        })
    );
  }, [scheduleFields, t]);
  const fieldActiveMap = useMemo(() => {
    return new Map(
      scheduleFields
        .filter((field) => ["field3", "field1", "field2", "approve1", "approve4", "field_t9"].includes(field.field_key))
        .map((field) => [field.field_key, field.active])
    );
  }, [scheduleFields]);

  // 検索条件設定の定義
  const scheduleSearchConditionDefinitions = useMemo<SearchConditionConfigDefinition<ScheduleSearchKeyType>[]>(
    () => [
      {
        itemsPerLine: 3,
        items: [
          { key: ScheduleSearchKey.ScheduleDate, label: t("scheduled_date"), required: true },
          { key: ScheduleSearchKey.Area, label: t("area"), required: false },
          { key: ScheduleSearchKey.Device, label: t("device"), required: false },
          { key: ScheduleSearchKey.Category, label: t("machines_category"), required: false },
          { key: ScheduleSearchKey.Manager, label: t("sankyu_manager"), required: false },
          fieldActiveMap.get("field3")
            ? { key: ScheduleSearchKey.Field3, label: fieldLabelMap.get("field3"), required: false }
            : undefined,
          fieldActiveMap.get("field1")
            ? { key: ScheduleSearchKey.Field1, label: fieldLabelMap.get("field1"), required: false }
            : undefined,
          fieldActiveMap.get("field2")
            ? { key: ScheduleSearchKey.Field2, label: fieldLabelMap.get("field2"), required: false }
            : undefined,
          fieldActiveMap.get("approve1") || fieldActiveMap.get("approve4")
            ? { key: ScheduleSearchKey.ApproveUnprocessed, label: t("unprocessed"), required: false }
            : undefined,
          fieldActiveMap.get("field_t9")
            ? { key: ScheduleSearchKey.FieldT9, label: fieldLabelMap.get("field_t9"), required: false }
            : undefined,
          fieldActiveMap.get("approve1")
            ? {
                key: ScheduleSearchKey.Approve1User,
                label: fieldLabelMap.get("approve1"),
                required: false,
              }
            : undefined,
          fieldActiveMap.get("approve4")
            ? { key: ScheduleSearchKey.Approve4User, label: fieldLabelMap.get("approve4"), required: false }
            : undefined,
        ].filter((v) => v),
      },
    ],
    [fieldActiveMap]
  );

  // 検索条件設定
  const config = useSearchConditionConfig<ScheduleSearchKeyType>({
    screenName: ScreenName.SCHEDULE,
    requiredKeys: scheduleSearchConditionDefinitions.flatMap((v) =>
      v.items.filter((item) => item.required).map((item) => item.key)
    ),
  });

  // 項目表示制御
  const activeFields = useMemo(() => {
    if (!config.map) return {};
    return {
      area: config.showAllConditions || !!config.map.get(ScheduleSearchKey.Area),
      device: config.showAllConditions || !!config.map.get(ScheduleSearchKey.Device),
      category: config.showAllConditions || !!config.map.get(ScheduleSearchKey.Category),
      manager: config.showAllConditions || !!config.map.get(ScheduleSearchKey.Manager),
      field3:
        !!fieldActiveMap.get("field3") && (config.showAllConditions || !!config.map.get(ScheduleSearchKey.Field3)),
      field1:
        !!fieldActiveMap.get("field1") && (config.showAllConditions || !!config.map.get(ScheduleSearchKey.Field1)),
      field2:
        !!fieldActiveMap.get("field2") && (config.showAllConditions || !!config.map.get(ScheduleSearchKey.Field2)),
      approve1Unprocessed:
        !!fieldActiveMap.get("approve1") &&
        (config.showAllConditions || !!config.map.get(ScheduleSearchKey.ApproveUnprocessed)),
      approve4Unprocessed:
        !!fieldActiveMap.get("approve4") &&
        (config.showAllConditions || !!config.map.get(ScheduleSearchKey.ApproveUnprocessed)),
      approve1User:
        !!fieldActiveMap.get("approve1") &&
        (config.showAllConditions || !!config.map.get(ScheduleSearchKey.Approve1User)),
      approve4User:
        !!fieldActiveMap.get("approve4") &&
        (config.showAllConditions || !!config.map.get(ScheduleSearchKey.Approve4User)),
      fieldT9:
        !!fieldActiveMap.get("field_t9") && (config.showAllConditions || !!config.map.get(ScheduleSearchKey.FieldT9)),
    };
  }, [config.showAllConditions, config.map, fieldActiveMap]);

  const filterSearchConditionByConfig = (condition?: SearchInput): SearchInput => {
    const params = condition || input.values();
    if (!config.map || config.showAllConditions) return params;

    return {
      schedule_date: params.schedule_date,
      area_id: activeFields.area ? params.area_id : [],
      device_id: activeFields.device ? params.device_id : [],
      category_id: activeFields.category ? params.category_id : [],
      manager_user_id: activeFields.manager ? params.manager_user_id : [],
      field3: activeFields.field3 ? params.field3 : "",
      field1: activeFields.field1 ? params.field1 : [],
      field2: activeFields.field2 ? params.field2 : "",
      approve1_unprocessed: activeFields.approve1Unprocessed ? params.approve1_unprocessed : false,
      approve4_unprocessed: activeFields.approve4Unprocessed ? params.approve4_unprocessed : false,
      approve1_user_id: activeFields.approve1User ? params.approve1_user_id : [],
      approve4_user_id: activeFields.approve4User ? params.approve4_user_id : [],
      field_t9: activeFields.fieldT9 ? params.field_t9 : false,
    };
  };

  // 検索条件保存
  const getCurrentConditionLabels = () => {
    // 現在の条件を保存モーダルに表示するために編集する
    const p = filterSearchConditionByConfig();
    const names: { label: string; values: string[] | null }[] = [
      {
        label: t("area"),
        values: p.area_id?.map((id) => masters?.areas.find((v) => v.area_id === Number(id))?.area_name),
      },
      {
        label: t("device"),
        values: p.device_id?.map((id) => masters?.devices.find((v) => v.device_id === Number(id))?.device_name),
      },
      {
        label: t("machines_category"),
        values: p.category_id?.map(
          (id) => masters?.categories.find((v) => v.category_id === Number(id))?.category_name
        ),
      },
      {
        label: t("sankyu_manager"),
        values: [masters?.users.find((v) => v.user_id === Number(p.manager_user_id))?.user_name],
      },
      {
        label: fieldLabelMap.get("field3"),
        values: [p.field3],
      },
      {
        label: fieldLabelMap.get("field1"),
        values: p.field1?.map((id) => hazardRankOptions.find((v) => v.id === id)?.name),
      },
      {
        label: fieldLabelMap.get("field2"),
        values: [attendanceOptions.find((v) => v.id === p.field2)?.name],
      },
      {
        label: t("unprocessed"),
        values: [
          p.approve1_unprocessed ? fieldLabelMap.get("approve1") : "",
          p.approve4_unprocessed ? fieldLabelMap.get("approve4") : "",
        ],
      },
      {
        label: fieldLabelMap.get("field_t9"),
        values: [p.field_t9 ? t("registered") : ""],
      },
      {
        label: fieldLabelMap.get("approve1"),
        values: p.approve1_user_id?.map((id) => masters?.users.find((v) => v.user_id === Number(id))?.user_name),
      },
      {
        label: fieldLabelMap.get("approve4"),
        values: p.approve4_user_id?.map((id) => masters?.users.find((v) => v.user_id === Number(id))?.user_name),
      },
    ];

    return names.map((n) => ({ label: n.label, values: _.compact(n.values) })).filter((n) => n.values?.length);
  };
  const currentSearchParams = useMemo<Omit<SearchInput, "schedule_date">>(() => {
    const searchParams = filterSearchConditionByConfig();
    return { ...searchParams, schedule_date: undefined };
  }, [
    input.areaId,
    input.deviceId,
    input.categoryId,
    input.managerUserId,
    input.field3,
    input.field1,
    input.field2,
    input.approve1Unprocessed,
    input.approve4Unprocessed,
    input.approve1UserId,
    input.approve4UserId,
    input.fieldT9,
    config.map,
    config.showAllConditions,
  ]);
  const handleApplyFilterConfig = (filter: FilterConfig<Omit<SearchInput, "schedule_date">>) => {
    // 検索条件設定で表示している検索条件のみ復元する
    const condition = filterSearchConditionByConfig({
      ...filter.config,
      schedule_date: input.values()?.schedule_date,
    });

    input.setScheduleDate(condition.schedule_date);
    setScheduleDateLabel(moment(condition.schedule_date).format("YYYY-MM-DD"));
    input.setAreaId(condition.area_id ?? []);
    input.setDeviceId(condition.device_id ?? []);
    input.setCategoryId(condition.category_id ?? []);
    input.setManagerUserId(condition.manager_user_id ?? []);
    input.setField3(condition.field3 ?? "");
    input.setField1(condition.field1 ?? []);
    input.setField2(condition.field2 ?? "");
    input.setApprove1Unprocessed(!!condition.approve1_unprocessed);
    input.setApprove4Unprocessed(!!condition.approve4_unprocessed);
    input.setFieldT9(!!condition.field_t9);
    input.setApprove1UserId(condition.approve1_user_id ?? []);
    input.setApprove4UserId(condition.approve4_user_id ?? []);
  };

  const hazardRankOptions = useMemo(
    () => searchOptions.find((v) => v.target === "field1")?.options ?? [],
    [searchOptions]
  );
  const attendanceOptions = useMemo(
    () => searchOptions.find((v) => v.target === "field2")?.options ?? [],
    [searchOptions]
  );

  const handleReset = () => {
    const date = moment().startOf("day").add(1, "day").toDate();
    setScheduleDateLabel(moment(date).format("YYYY/MM/DD"));
    onReset();
    dispatch(actions.filterConfig.clearSelectedFilterConfig());
  };

  const handleSearch = () => {
    if (!scheduleDateLabel) {
      dispatch(
        actions.app.showAlert(t("error"), [`${t("scheduled_date")}${t("input_required_field")}`], () =>
          dispatch(actions.app.hideAlert())
        )
      );

      return;
    }

    onSearch({
      params: {
        schedule_date: moment(scheduleDateLabel, "YYYY/MM/DD").toDate(),
        area_id: activeFields.area ? input.areaId : [],
        device_id: activeFields.device ? input.deviceId : [],
        category_id: activeFields.category ? input.categoryId : [],
        manager_user_id: activeFields.manager ? input.managerUserId : [],
        field3: activeFields.field3 ? input.field3 : "",
        field1: activeFields.field1 ? input.field1 : [],
        field2: activeFields.field2 ? input.field2 : "",
        approve1_unprocessed: activeFields.approve1Unprocessed ? input.approve1Unprocessed : false,
        approve4_unprocessed: activeFields.approve4Unprocessed ? input.approve4Unprocessed : false,
        approve1_user_id: activeFields.approve1User ? input.approve1UserId : [],
        approve4_user_id: activeFields.approve4User ? input.approve4UserId : [],
        field_t9: activeFields.fieldT9 ? input.fieldT9 : false,
      },
    });
  };

  if (!config.map) return <></>;

  return (
    <>
      <div>
        <div className="flex justify-between bg-[#e4e4e4] w-[900px]">
          {/* 検索条件保存 (Filter config) */}
          <FilterConfigGroup
            screenName={ScreenName.SCHEDULE}
            getCurrentConditionLabels={getCurrentConditionLabels}
            currentConfig={currentSearchParams}
            applyFilterConfig={handleApplyFilterConfig}
            data-test-id-prefix="schedule"
          />
          {/* 検索条件設定 (Search condition config) */}
          <SettingIconButton
            className="w-[32px] p-[8px]"
            onClick={config.openSettingModal}
            data-test-id="schedule-search-button-setting"
          />
        </div>
        <div className="search-box w-900 mb-0">
          <FormGrid className="grid-cols-3">
            <FormGroup title={t("scheduled_date")} titleClassName="w-100">
              <DatePicker
                wrapperClassName="w-[100%]"
                selected={!scheduleDateLabel ? null : moment(scheduleDateLabel, "YYYY/MM/DD").toDate()}
                dateFormat="yyyy/MM/dd"
                locale={t("calender_locale")}
                onChange={(d) => {
                  let date = d;
                  if (!d) {
                    // 翌日の日付がデフォルト
                    const today = new Date();
                    date = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1);
                  }
                  input.setScheduleDate(date);
                  setScheduleDateLabel(!date ? null : moment(date).format("YYYY-MM-DD"));
                }}
                customInput={<CustomDatePickerInput data-test-id="schedule-search-schedule-date" />}
              />
            </FormGroup>
            <FormGroup title={t("area")} display={activeFields.area}>
              <MasterSelect
                prefix="area"
                isMulti={true}
                options={masters?.areas ?? []}
                value={input.areaId}
                onChange={(ids) => input.setAreaId(ids)}
                data-test-id="schedule-search-area"
              />
            </FormGroup>
            <FormGroup title={t("device")} display={activeFields.device}>
              <MasterSelect
                prefix="device"
                isMulti={true}
                options={masters?.devices ?? []}
                value={input.deviceId}
                onChange={(ids) => input.setDeviceId(ids)}
                data-test-id="schedule-search-device"
              />
            </FormGroup>
            <FormGroup title={t("machines_category")} titleClassName="w-100" display={activeFields.category}>
              <MasterSelect
                prefix="category"
                isMulti={true}
                options={masters?.categories ?? []}
                value={input.categoryId ?? NONE_VALUE}
                onChange={(ids) => input.setCategoryId(ids)}
                data-test-id="schedule-search-category"
              />
            </FormGroup>
            <FormGroup title={t("sankyu_manager")} display={activeFields.manager}>
              <MasterSelect
                prefix="user"
                isMulti={true}
                options={masters?.users ?? []}
                value={input.managerUserId}
                onChange={(ids) => input.setManagerUserId(ids)}
                data-test-id="schedule-search-manager-user"
              />
            </FormGroup>
            <FormGroup title={fieldLabelMap.get("field3")} wrapTitle={true} display={activeFields.field3}>
              <input
                type="text"
                className="form-control"
                value={input.field3}
                onChange={(e) => input.setField3(e.target.value)}
                data-test-id="schedule-search-field3"
              />
            </FormGroup>
            <FormGroup
              title={fieldLabelMap.get("field1")}
              wrapTitle={true}
              titleClassName="w-100"
              display={activeFields.field1}
            >
              <MasterSelect
                isMulti={true}
                options={hazardRankOptions}
                value={input.field1}
                onChange={(ids) => input.setField1(ids)}
                placeholder={<span className="text-black">{"-----"}</span>}
                data-test-id="schedule-search-field1"
              />
            </FormGroup>
            <FormGroup title={fieldLabelMap.get("field2")} wrapTitle={true} display={activeFields.field2}>
              <MasterSelect
                options={attendanceOptions}
                value={input.field2}
                onChange={(ids) => input.setField2(ids[0])}
                withNone={true}
                noneLabel={"-----"}
                placeholder={<span className="text-black">{"-----"}</span>}
                data-test-id="schedule-search-field2"
              />
            </FormGroup>
            <div
              className={clsx(
                "flex flex-col flex-start",
                !activeFields.approve1Unprocessed && !activeFields.approve4Unprocessed && !activeFields.fieldT9
                  ? "hidden"
                  : ""
              )}
            >
              <FormRow
                className="whitespace-nowrap"
                display={activeFields.approve1Unprocessed || activeFields.approve4Unprocessed}
              >
                <span className="form-label w-[82px] mr-[8px] text-right">{t("unprocessed")}</span>
                {activeFields.approve1Unprocessed && (
                  <label className="ckbox mr-10" data-test-id="schedule-search-filter1">
                    <input
                      type="checkbox"
                      checked={input.approve1Unprocessed}
                      onChange={() => input.setApprove1Unprocessed(!input.approve1Unprocessed)}
                    />
                    <span className="w-[70px] inline-block whitespace-pre-wrap">{fieldLabelMap.get("approve1")}</span>
                  </label>
                )}
                {activeFields.approve4Unprocessed && (
                  <label className="ckbox mr-0" data-test-id="schedule-search-filter2">
                    <input
                      type="checkbox"
                      checked={input.approve4Unprocessed}
                      onChange={() => input.setApprove4Unprocessed(!input.approve4Unprocessed)}
                    />
                    <span className="w-[70px] inline-block whitespace-pre-wrap">{fieldLabelMap.get("approve4")}</span>
                  </label>
                )}
              </FormRow>
              <FormRow className="whitespace-nowrap" display={activeFields.fieldT9}>
                <span className="form-label w-[82px] mr-[8px] pl-[8px] text-right whitespace-pre-wrap">
                  {fieldLabelMap.get("field_t9")}
                </span>
                <label className="ckbox" data-test-id="schedule-search-field-t9">
                  <input
                    type="checkbox"
                    checked={input.fieldT9 || false}
                    onChange={() => input.setFieldT9(!input.fieldT9)}
                  />
                  <span>{t("registered")}</span>
                </label>
              </FormRow>
            </div>
            <FormGroup
              title={fieldLabelMap.get("approve1")}
              wrapTitle={true}
              titleClassName="w-100"
              display={activeFields.approve1User}
            >
              <MasterSelect
                prefix="user"
                isMulti={true}
                options={masters?.users ?? []}
                value={input.approve1UserId}
                onChange={(ids) => input.setApprove1UserId(ids)}
                data-test-id="schedule-search-approve1-user"
              />
            </FormGroup>
            <FormGroup title={fieldLabelMap.get("approve4")} wrapTitle={true} display={activeFields.approve4User}>
              <MasterSelect
                prefix="user"
                isMulti={true}
                options={masters?.users ?? []}
                value={input.approve4UserId}
                onChange={(ids) => input.setApprove4UserId(ids)}
                data-test-id="schedule-search-approve4-user"
              />
            </FormGroup>
          </FormGrid>
          <FormRow className="justify-between mt-[16px]">
            <Spacer x="210px" />
            <SearchConditionDisplayButton
              isExpanded={config.showAllConditions}
              onChange={config.toggleDisplayOptionalConditions}
              data-test-id="schedule-search-button-condition"
            />
            <div className="flex gap-x-4">
              <button data-test-id="schedule-search-button-reset" className="btn btn-gray" onClick={handleReset}>
                {t("reset")}
              </button>
              <button data-test-id="schedule-search-button-search" className="btn btn-blue" onClick={handleSearch}>
                {t("search")}
              </button>
            </div>
          </FormRow>
        </div>
        {config.showSettingModal && (
          <SearchConditionConfigModal<ScheduleSearchKeyType>
            title={t("search_condition_config")}
            configMap={config.map}
            definitions={scheduleSearchConditionDefinitions}
            onClose={config.closeSettingModal}
            onSubmit={(c) => config.saveSearchItemConfig(c)}
          />
        )}
      </div>
      <div className="ml-20">
        <ExplanatoryNote />
      </div>
      <div className="ml-20">
        <FireTypeAbbreviationNote />
      </div>
    </>
  );
};
